/**
 * Brand Brain persistence — load / save / subscribe for the ORG-level
 * BrandBrain document.
 *
 * Per the projection-layer contract (./store.ts): NO new Store-interface
 * methods. The whole BrandBrain rides the shipped `db` store as ONE
 * JSON-safe record inside the existing `brainFacts` collection — a
 * synthetic org-scoped fact (`accountId: '__org__'`) whose `value` is the
 * serialized BrandBrain. That keeps Firestore rules, the localStorage
 * adapter, and the Store interface all untouched.
 *
 * The (de)serialization half is PURE and total — parseBrandBrain never
 * throws and degrades any malformed payload to the honest empty brain, so
 * a corrupt doc can never brick the editor or the prompt-composition path.
 */

import { db, nowIso } from '../db/store';
import type { BrainFact } from '../db/types';
import {
  emptyBrandBrain,
  type BrandBrain,
  type BrandEntry,
  type BrandEntryKind,
  type BrandScope,
  type BrandStatus,
} from './schema';

// ---------------------------------------------------------------------------
// Storage identity
// ---------------------------------------------------------------------------

/** Synthetic account id — the Brand Brain is org-wide, not per-account. */
export const BRAND_BRAIN_ACCOUNT_ID = '__org__';

/** Deterministic doc id (mirrors the `${accountId}:${section}:${field}`
 *  convention brainFacts already uses), so every save upserts one row. */
export const BRAND_BRAIN_FACT_ID = `${BRAND_BRAIN_ACCOUNT_ID}:rules:brandBrain`;

// ---------------------------------------------------------------------------
// Pure (de)serialization
// ---------------------------------------------------------------------------

const KINDS: readonly BrandEntryKind[] = [
  'voice',
  'positioning',
  'offer',
  'directive',
  'lexicon',
  'proof',
];
const SCOPES: readonly BrandScope[] = ['content', 'proposal', 'blog', 'email', 'all'];
const STATUSES: readonly BrandStatus[] = ['active', 'draft', 'archived'];

/** Serialize a BrandBrain to the JSON string stored in the fact's value. */
export function serializeBrandBrain(brain: BrandBrain): string {
  return JSON.stringify(brain);
}

function coerceEntry(raw: unknown): BrandEntry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  // Required identity + text fields must be real strings.
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (typeof r.title !== 'string' || typeof r.body !== 'string') return null;
  const kind = KINDS.includes(r.kind as BrandEntryKind)
    ? (r.kind as BrandEntryKind)
    : 'voice';
  const status = STATUSES.includes(r.status as BrandStatus)
    ? (r.status as BrandStatus)
    : 'active';
  const scopes = Array.isArray(r.scopes)
    ? (r.scopes.filter((s) => SCOPES.includes(s as BrandScope)) as BrandScope[])
    : [];
  return {
    id: r.id,
    kind,
    title: r.title,
    body: r.body,
    weight: typeof r.weight === 'number' && Number.isFinite(r.weight) ? r.weight : 1,
    scopes: scopes.length > 0 ? scopes : ['all'],
    status,
    updatedBy: typeof r.updatedBy === 'string' ? r.updatedBy : 'operator',
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
  };
}

/**
 * Parse an untrusted serialized payload back into a BrandBrain. TOTAL:
 * never throws — malformed JSON or a wrong shape returns the empty brain,
 * and individually-invalid entries are dropped rather than poisoning the
 * rest.
 */
export function parseBrandBrain(raw: unknown): BrandBrain {
  if (typeof raw !== 'string' || raw.trim() === '') return emptyBrandBrain;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyBrandBrain;
  }
  if (typeof data !== 'object' || data === null) return emptyBrandBrain;
  const entriesRaw = (data as { entries?: unknown }).entries;
  if (!Array.isArray(entriesRaw)) return emptyBrandBrain;
  const entries = entriesRaw
    .map(coerceEntry)
    .filter((e): e is BrandEntry => e !== null);
  return { entries };
}

// ---------------------------------------------------------------------------
// IO — rides the shipped db store, one upserted row
// ---------------------------------------------------------------------------

function toFact(brain: BrandBrain, updatedBy: string, version: number): BrainFact {
  return {
    id: BRAND_BRAIN_FACT_ID,
    accountId: BRAND_BRAIN_ACCOUNT_ID,
    section: 'rules',
    field: 'brandBrain',
    label: 'Brand Brain',
    value: serializeBrandBrain(brain),
    source: 'operator',
    confidence: 1,
    version,
    setBy: updatedBy || 'operator',
    setAt: nowIso(),
  };
}

/** One-shot read of the org Brand Brain; empty brain when unset/corrupt. */
export async function loadBrandBrain(): Promise<BrandBrain> {
  const fact = await db.get('brainFacts', BRAND_BRAIN_FACT_ID);
  return parseBrandBrain(fact?.value);
}

/** Upsert the whole org Brand Brain (bumps the fact's version). */
export async function saveBrandBrain(
  brain: BrandBrain,
  updatedBy = 'operator',
): Promise<void> {
  const existing = await db.get('brainFacts', BRAND_BRAIN_FACT_ID);
  await db.put('brainFacts', toFact(brain, updatedBy, (existing?.version ?? 0) + 1));
}

/** Live subscription to the org Brand Brain; returns an unsubscribe fn. */
export function subscribeBrandBrain(cb: (brain: BrandBrain) => void): () => void {
  return db.subscribe('brainFacts', (facts) => {
    const fact = facts.find((f) => f.id === BRAND_BRAIN_FACT_ID);
    cb(parseBrandBrain(fact?.value));
  });
}
